import { LEGAL, type LegalSet } from './meta';

const { company: C, email: E } = LEGAL;

export const es: LegalSet = {
  privacy: {
    title: 'Política de privacidad',
    intro: `Esta política explica qué datos personales recopila ${C} ("nosotros") cuando utilizas el sitio web, el editor y la API (el "Servicio"), por qué los recopilamos y qué derechos te asisten.`,
    sections: [
      {
        h: '1. Datos que recopilamos',
        p: [
          'Datos de la cuenta: nombre, dirección de correo electrónico, hash de la contraseña, idioma de la interfaz y, si inicias sesión con Google, el identificador de tu cuenta de Google y tu correo electrónico verificado.',
          'Contenido: plantillas, imágenes, fuentes, tablas y otros archivos que subes, los datos que envías a la API para generar imágenes y las imágenes generadas.',
          'Datos de uso: imágenes generadas, ejecuciones de automatizaciones, solicitudes de IA, claves de API (almacenadas como hashes), sesiones iniciadas, dirección IP y agente de usuario del navegador, registros de errores.',
          'Datos de facturación: plan, estado de la suscripción e identificadores de pago. Los datos de la tarjeta los gestiona el proveedor de pagos (Lemon Squeezy o Stripe) y nunca llegan a nuestros servidores.',
        ],
      },
      {
        h: '2. Cómo utilizamos los datos',
        p: [
          'Para prestar el Servicio: almacenar tus plantillas, generar imágenes, ejecutar automatizaciones y entregar los resultados a las integraciones que conectes.',
          'Para mantener el Servicio seguro y fiable: autenticación, prevención de abusos, límites de solicitudes, depuración y copias de seguridad.',
          'Para facturarte y aplicar los límites del plan, y para enviarte correos del servicio (restablecimiento de contraseña, invitaciones, cambios importantes). No enviamos correos de marketing sin tu consentimiento.',
        ],
      },
      {
        h: '3. Bases jurídicas',
        p: [
          'Tratamos los datos para ejecutar el contrato que tenemos contigo, para cumplir obligaciones legales y sobre la base de nuestro interés legítimo en ofrecer un servicio seguro. Cuando la ley lo exige, solicitamos tu consentimiento.',
        ],
      },
      {
        h: '4. Funciones de IA',
        p: [
          'Cuando utilizas la generación con IA, la descripción o el texto que introduces se envía a nuestro proveedor de IA (Google Gemini API) para obtener el resultado. No enviamos tus demás plantillas ni archivos. El proveedor trata estos datos conforme a sus propias condiciones y no utiliza el tráfico de pago de la API para entrenar sus modelos.',
        ],
      },
      {
        h: '5. Comunicación de datos',
        p: [
          'No vendemos datos personales. Solo los compartimos con encargados del tratamiento que nos ayudan a operar el Servicio —alojamiento y almacenamiento, pagos (Lemon Squeezy, Stripe), envío de correo electrónico, generación con IA (Google)— y con las integraciones que tú mismo conectes (Google Drive, Sheets, S3, webhooks).',
          'Podemos revelar datos cuando lo exija la ley o para proteger a nuestros usuarios y el Servicio.',
          'Algunos encargados (por ejemplo, Google) pueden tratar datos fuera del EEE. Estas transferencias se basan en el Marco de Privacidad de Datos UE-EE. UU. o en las Cláusulas Contractuales Tipo de la Comisión Europea.',
        ],
      },
      {
        h: '6. Conservación',
        p: [
          'Conservamos los datos de la cuenta y el contenido mientras tu cuenta esté activa. Las imágenes generadas y los registros se conservan según tu plan y tu configuración. Una vez que eliminas un espacio de trabajo o una cuenta, los datos se eliminan en un plazo de 30 días, salvo que la ley nos obligue a conservarlos durante más tiempo (por ejemplo, las facturas).',
        ],
      },
      {
        h: '7. Cookies',
        p: [
          'Cookies esenciales (no requieren consentimiento): la cookie de sesión que mantiene tu sesión iniciada y una cookie de corta duración para el inicio de sesión con Google. Guardamos tu idioma, el espacio de trabajo seleccionado y tu elección sobre cookies en el navegador.',
          'Cookies analíticas, solo con tu consentimiento: Google Analytics 4 (Google Ireland Ltd.) mide las visitas con identificadores seudónimos en las cookies _ga (hasta 2 años); Google conserva los datos 14 meses y las funciones publicitarias y Google Signals están desactivadas. La analítica no se carga hasta que la aceptas en el banner de cookies, y puedes retirar el consentimiento en cualquier momento desde «Configuración de cookies» en el pie de página o en tu perfil. No utilizamos cookies publicitarias.',
        ],
      },
      {
        h: '8. Seguridad',
        p: [
          'Las contraseñas y las claves de API se almacenan como hashes, las credenciales de las integraciones se cifran y el tráfico se protege mediante HTTPS. Ningún sistema es completamente seguro, pero adoptamos medidas razonables para proteger tus datos.',
        ],
      },
      {
        h: '9. Tus derechos',
        p: [
          `Tienes derecho de acceso, rectificación, supresión, limitación, portabilidad y oposición (arts. 15–21 del RGPD). En Configuración → Perfil puedes descargar una copia de tus datos y eliminar tu cuenta tú mismo. También puedes oponerte al tratamiento, retirar tu consentimiento y presentar una reclamación ante tu autoridad de protección de datos. Para ejercer tus derechos, escríbenos a ${E}.`,
        ],
      },
      {
        h: '10. Menores',
        p: [
          'El Servicio no está dirigido a menores de 16 años y no recopilamos conscientemente sus datos.',
        ],
      },
      {
        h: '11. Cambios y contacto',
        p: [
          `Publicaremos los cambios en esta página y te avisaremos por correo electrónico de los que sean significativos. Consultas sobre privacidad: ${E}.`,
        ],
      },
    ],
  },
  terms: {
    title: 'Condiciones del servicio',
    intro: `Estas condiciones regulan el uso de ${C} (el "Servicio"). Al crear una cuenta o utilizar el Servicio, las aceptas. Si utilizas el Servicio en nombre de una empresa, las aceptas en su nombre.`,
    sections: [
      {
        h: '1. El Servicio',
        p: [
          'El Servicio te permite diseñar plantillas de imágenes, generar imágenes mediante el editor, la API y las automatizaciones, y utilizar funciones de IA. Podemos modificar y mejorar las funciones con el tiempo.',
        ],
      },
      {
        h: '2. Cuenta',
        p: [
          'Debes proporcionar información veraz y mantener la confidencialidad de tu contraseña y de tus claves de API. Eres responsable de todo lo que se haga a través de tu cuenta y de tus espacios de trabajo. Avísanos de inmediato si sospechas de un acceso no autorizado.',
        ],
      },
      {
        h: '3. Planes, pago y cancelación',
        p: [
          'Los planes de pago se facturan por adelantado, de forma mensual o anual, y se renuevan automáticamente. Los pagos los procesa nuestro revendedor Lemon Squeezy (el comerciante registrado, que también gestiona los impuestos sobre las ventas) o Stripe. Te enviamos un recordatorio por correo electrónico unos días antes de cada renovación y un recibo después de ella. Los límites de uso de cada plan se indican en la página de precios; cuando se alcanza un límite, las acciones correspondientes quedan en pausa hasta el siguiente periodo o hasta que mejores el plan.',
          'Si falla el pago de una renovación, el plan sigue funcionando durante varios días mientras actualizas el método de pago; si el pago sigue sin realizarse, el espacio de trabajo pasa al plan Free. Tus plantillas y archivos se conservan.',
          'Puedes cancelar en cualquier momento: el plan seguirá activo hasta el final del periodo pagado y no se renovará. Los pagos no son reembolsables, salvo cuando la ley lo exija. Podemos modificar los precios con un preaviso mínimo de 30 días.',
        ],
      },
      {
        h: '4. Tu contenido',
        p: [
          'Conservas todos los derechos sobre las plantillas, los archivos y los datos que subes y sobre las imágenes que generas. Nos concedes una licencia limitada para almacenar, tratar y mostrar este contenido con el único fin de prestarte el Servicio.',
          'Eres responsable de disponer de los derechos sobre el contenido que utilizas, incluidas imágenes, fuentes, logotipos y marcas comerciales.',
        ],
      },
      {
        h: '5. Uso aceptable',
        p: [
          'No debes utilizar el Servicio para crear o difundir contenido ilícito, contenido que vulnere derechos de terceros, software malicioso, spam o materiales engañosos, contenido que explote a menores, ni para acosar a otras personas.',
          'No debes intentar dañar o sobrecargar el Servicio, eludir los límites o las medidas de seguridad, ni revender el acceso sin nuestro permiso. Podemos suspender las cuentas que infrinjan estas normas.',
        ],
      },
      {
        h: '6. Funciones de IA',
        p: [
          'Los resultados de la IA se generan automáticamente y pueden ser inexactos o parecidos a resultados obtenidos por otras personas. Revísalos antes de publicarlos; eres responsable del uso que hagas de ellos.',
        ],
      },
      {
        h: '7. Plan Free y vistas previas',
        p: [
          'Las vistas previas son gratuitas y siempre llevan marca de agua. El plan Free se ofrece tal cual y sus límites pueden cambiar.',
        ],
      },
      {
        h: '8. Disponibilidad',
        p: [
          'Procuramos ofrecer una alta disponibilidad, pero no garantizamos que el Servicio funcione sin interrupciones ni errores. Guarda copias de los datos importantes; puedes exportar las plantillas en cualquier momento.',
        ],
      },
      {
        h: '9. Limitación de responsabilidad',
        p: [
          'El Servicio se presta "tal cual". En la medida en que lo permita la ley, no somos responsables de daños indirectos, lucro cesante ni pérdida de datos, y nuestra responsabilidad total se limita al importe que nos hayas pagado en los 12 meses anteriores a la reclamación.',
        ],
      },
      {
        h: '10. Terminación',
        p: [
          'Puedes eliminar tus espacios de trabajo y dejar de utilizar el Servicio en cualquier momento. Podemos suspender o cancelar el acceso por incumplimiento de estas condiciones o por impago; cuando sea posible, te avisaremos con antelación y te permitiremos exportar tus datos.',
        ],
      },
      {
        h: '11. Cambios y contacto',
        p: [
          `Podemos actualizar estas condiciones; te avisaremos con antelación de los cambios significativos. Si sigues utilizando el Servicio después de que los cambios entren en vigor, se entenderá que los aceptas. Consultas: ${E}.`,
        ],
      },
    ],
  },
};
